import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';

export interface ImportResult {
  imported: number;
  skipped?: number;
  errors: Array<{ row: number; error: string }>;
}

export interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImportComplete: () => void;
  title: string;
  description: string;
  templateFileName: string;
  onDownloadTemplate: () => Promise<Blob>;
  onImport: (file: File) => Promise<ImportResult>;
  entityLabel?: string;
  children?: React.ReactNode;
}

export const ImportDialog: React.FC<ImportDialogProps> = ({
  open,
  onClose,
  onImportComplete,
  title,
  description,
  templateFileName,
  onDownloadTemplate,
  onImport,
  entityLabel = 'registro(s)',
  children,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] ?? null;
    setFile(selectedFile);
    setError(null);
    setResult(null);
  };

  const handleDownloadTemplate = async () => {
    setTemplateLoading(true);
    try {
      const blob = await onDownloadTemplate();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.download = templateFileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Erro ao baixar template');
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError('Selecione um arquivo para importar');
      return;
    }

    setError(null);
    setResult(null);
    setLoading(true);

    try {
      const importResult = await onImport(file);
      setResult(importResult);
      if (importResult.imported > 0) {
        onImportComplete();
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao importar';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setError(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UploadIcon />
          <Typography variant="h6">{title}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {description}
        </Typography>

        {children}

        <Button
          variant="outlined"
          startIcon={templateLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
          onClick={handleDownloadTemplate}
          disabled={templateLoading}
          sx={{ mb: 3 }}
          fullWidth
        >
          {templateLoading ? 'Baixando...' : 'Baixar Template de Importação'}
        </Button>

        <Box
          sx={{
            border: '2px dashed',
            borderColor: file ? 'primary.main' : 'grey.400',
            borderRadius: 1,
            p: 3,
            textAlign: 'center',
            mb: 2,
            cursor: 'pointer',
            '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body1">
            {file ? file.name : 'Clique para selecionar um arquivo'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Formatos aceitos: .xlsx, .xls
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {result && (
          <Box>
            <Alert severity={result.errors.length > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>{result.imported}</strong> {entityLabel} importado(s)
              </Typography>
              {(result.skipped ?? 0) > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {result.skipped} {entityLabel} já existente(s) foram ignorados.
                </Typography>
              )}
              {result.errors.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {result.errors.length} erro(s) encontrado(s).
                </Typography>
              )}
            </Alert>

            {result.errors.length > 0 && (
              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Erros:
                </Typography>
                <List dense>
                  {result.errors.map((err, index) => (
                    <ListItem key={index} disablePadding>
                      <ListItemText
                        primary={`Linha ${err.row}: ${err.error}`}
                        primaryTypographyProps={{ variant: 'caption', color: 'error' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {result ? 'Fechar' : 'Cancelar'}
        </Button>
        {!result && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={loading || !file}
            startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            {loading ? 'Importando...' : 'Importar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportDialog;
